import { destroyCookie, parseCookies, setCookie } from 'nookies';
import { TRACKING_COOKIE_MAX_AGE_SECONDS } from '../../customerSourceAnalytics/customerSourceAnalytics.constants';

const CRITEO_FPID_COOKIE_NAME = 'criteo_fpid';
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const COOKIE_DOMAIN = '.sweepking.com';
export const CRITEO_FPID_HEADER_NAME = 'x-criteo-fpid';

function getCookieDomain(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const { hostname } = window.location;
  return hostname === 'sweepking.com' || hostname.endsWith(COOKIE_DOMAIN)
    ? COOKIE_DOMAIN
    : undefined;
}

function getCookieOptions() {
  return {
    path: '/',
    maxAge: TRACKING_COOKIE_MAX_AGE_SECONDS,
    sameSite: 'lax' as const,
    domain: getCookieDomain(),
  };
}

function isValidFpid(value: string | undefined): value is string {
  return !!value && UUID_V4_PATTERN.test(value);
}

function generateUuidV4(): string | null {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return null;
}

function persistCriteoFpid(value: string): void {
  setCookie(null, CRITEO_FPID_COOKIE_NAME, value, getCookieOptions());
}

export function readExistingCriteoFpid(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const cookieValue = parseCookies()[CRITEO_FPID_COOKIE_NAME];
  if (isValidFpid(cookieValue)) {
    return cookieValue;
  }

  return null;
}

export function storeIncomingCriteoFpid(value: string | null | undefined): string | null {
  const normalizedValue = typeof value === 'string' ? value.trim() : undefined;
  if (!isValidFpid(normalizedValue)) {
    return null;
  }

  persistCriteoFpid(normalizedValue);
  return normalizedValue;
}

export const getCriteoFpidParam = (): string | null => {
  if (typeof window === 'undefined' || !window.Cookiebot?.consent?.statistics) {
    return null;
  }

  return readExistingCriteoFpid();
};

export function getOrCreateCriteoFpid(): string | null {
  const existingFpid = readExistingCriteoFpid();
  if (existingFpid) {
    persistCriteoFpid(existingFpid);
    return existingFpid;
  }

  const fpid = generateUuidV4();
  if (!fpid) {
    return null;
  }

  persistCriteoFpid(fpid);
  return fpid;
}

export function clearCriteoFpid(): void {
  destroyCookie(null, CRITEO_FPID_COOKIE_NAME, { path: '/', domain: getCookieDomain() });
  destroyCookie(null, CRITEO_FPID_COOKIE_NAME, { path: '/' });
}
